import type {
  BlockUiSchema,
  GroupUiSchema
} from '@epilot/journey-logic-commons'
import {
  getBlockNameByScope,
  JourneyFeatureFlags
} from '@epilot/journey-logic-commons'
import {
  findLayoutZoneOfEachBlock,
  isActionBarBlock
} from '@epilot/journey-logic-commons/src/next/journey'
import { isBlockReference } from '@epilot/journey-logic-commons/src/next/references'
import { selectSortableMapSorted } from '@epilot/journey-logic-commons/src/next/store'
import { ACTION_BAR_ACTION_TYPE } from '@epilot/journey-logic-commons/src/types/next'
import type {
  Journey,
  Block,
  BlockId,
  Step,
  StepId,
  Reference
} from '@epilot/journey-logic-commons/src/types/next'
import { memoLastCall } from 'radashi'

import type { RuleRef } from '../../services/journey-service'
import type {
  BlockValue,
  ContextualValues,
  DesiredState,
  PerJourneyAppState,
  WritableAppState
} from '../types'
import { SUBMISSION_STATUS } from '../types/enums'

import {
  selectBlockById,
  selectContextParametersByKey,
  selectFeatureFlag,
  selectBlockByIdOrScope,
  selectIsAppReady,
  selectJourney,
  selectNumberOfSteps,
  selectSelected,
  selectStepById,
  selectRoot
} from './config'

export const selectAppError = (state: WritableAppState) => state.appError

export const selectAppParams = (state: WritableAppState) => state.appParams

/**
 * Same as {@link selectAppParams}, but throws an error if not set.
 */
export const selectAppParamsOrThrows = (state: WritableAppState) => {
  const appParams = selectAppParams(state)

  if (!appParams) throw new Error('Expected App params to be set')

  return appParams
}

export const selectIsPreview = (state: WritableAppState) =>
  Boolean(state.appParams?.preview)

export const selectJourneyStepsSorted = (state: WritableAppState) => {
  const journey = selectJourney(state)

  return selectSortableMapSorted<StepId, Step>(journey?.steps)
}

/**
 * @deprecated please use {@link selectDesign} instead
 */
export const selectDesignBuilderState = (state: WritableAppState) =>
  state.designBuilderState

/**
 * Returns the design details for the given Journey while falling back
 * to design details embedded in the Config.
 */
export const selectDesign = (state: WritableAppState) => {
  const designOverride = selectDesignBuilderState(state)

  if (selectIsPreview(state) && designOverride) return designOverride

  const selected = selectSelected(state)
  const root = selectRoot(state)

  return (
    selected?.design ??
    selected?.journey?.design ??
    root?.design ??
    root?.journey?.design
  )
}

/**
 * Similar to {@link selectDesign}, but always returns the design of the Root.
 */
export const selectRootDesign = (state: WritableAppState) => {
  const designOverride = selectDesignBuilderState(state)

  if (selectIsPreview(state) && designOverride) return designOverride

  const root = selectRoot(state)

  return root?.design ?? root?.journey?.design
}

/**
 * Indicates whether design is known for the selected or root Journey.
 *
 * Note: Remember that templates will not have a design set.
 */
export const selectHasDesign = (state: WritableAppState) => {
  const theme = selectDesign(state)?.theme

  if (theme) return Object.keys(theme).length > 0

  return false
}

export const selectDarkMode = (state: WritableAppState) => state.darkMode

export const selectBlockValues = (state: WritableAppState) => state.blockValues

export const selectBlockValueById = (state: WritableAppState, id: BlockId) => {
  return selectBlockValues(state)?.[id]
}

export const selectLastDesiredState = (
  state: WritableAppState
): DesiredState | undefined => {
  return state.logicsEngine.history.slice(-1)?.[0]
}

/**
 * Determines if the logics engine is locked.
 * Note: If the app is not ready, the engine is considered as locked.
 */
export const selectLogicsLock = (state: WritableAppState): boolean => {
  return !selectIsAppReady(state) || state.logicsEngine.locked
}

export const selectShownBlocks = (state: WritableAppState): Set<BlockId> => {
  return selectLastDesiredState(state)?.show ?? new Set<BlockId>()
}

export const selectCurrentValidationRules = (
  state: WritableAppState,
  blockId: BlockId | undefined
): RuleRef | undefined => {
  const journey = selectJourney(state)

  if (!blockId) {
    return undefined
  }

  if (journey?.type === 'regular' && journey.validationRules) {
    return journey.validationRules[blockId]
  }

  return undefined
}

/**
 * Determines if a block is supposed to be shown or not.
 *
 * A block should be shown if:
 *
 * - Doesn't contain associated display conditions
 * - or if the display condition evaluted to true by the logics engine
 */
export const selectIsBlockShown = (
  state: WritableAppState,
  /** Id of the given block */
  id: BlockId | undefined,
  /** Scope of the given block, which is used as a search fallback */
  scope: string | undefined
): boolean | undefined => {
  if (state.appParams?.preview) return true

  // assumes the blocks visibility is undefined if running the old engine
  if (!selectFeatureFlag(state, JourneyFeatureFlags.LOGICS_ENGINE)) {
    return undefined
  }

  let block: Block | undefined

  if (typeof id === 'undefined') {
    const name = getBlockNameByScope(scope)

    if (!name) return false

    const currentStep = selectCurrentStep(state)

    if (!currentStep) return false

    block = selectBlocksMapCache(state)?.get(currentStep.orderIndex)?.get(name)
  } else {
    // if id is provided, use it directly
    block = selectBlockById(state, id)
  }

  const refId = block?.parent
    ? selectBlockById(state, block.parent)?.id
    : block?.id

  if (typeof refId === 'undefined') return false

  return (
    // doesn't have a display condition
    selectSelected(state)?.displayConditionBlocks?.has(refId) !== true ||
    // or is set to be shown by the logics engine
    selectLastDesiredState(state)?.show?.has(refId) === true
  )
}

/**
 * For a given set of uischema elements, returns a map keyed by their Block Id or Scope
 * and a value indicating whether the block is shown or not.
 *
 * @deprecated to be sunset with json forms and the old data structure
 */
export const selectShownLayoutBlocks = (
  state: WritableAppState,
  elements: (BlockUiSchema | GroupUiSchema)[]
): {
  [key: BlockId | string]: boolean | undefined
} => {
  return elements.reduce<{
    [key: string]: boolean | undefined
  }>((acc, uischema) => {
    acc[uischema.id ?? uischema.scope] = selectIsBlockShown(
      state,
      uischema.id,
      uischema.scope
    )

    return acc
  }, {})
}

/**
 * Return the blocks that are hidden in the current step because of display conditions.
 * If a stepId is provided, it will only return blocks that belong to that step.
 * If no stepId is provided, it will return all hidden blocks in the journey.
 * This is useful for determining which blocks are not visible to the user
 */
export const selectHiddenBlocks = (
  state: WritableAppState,
  stepId?: string
): Record<string, Block> => {
  // assumes the blocks visibility is undefined if running the old engine
  if (!selectFeatureFlag(state, JourneyFeatureFlags.LOGICS_ENGINE)) {
    return {}
  }

  const blocks = selectSelected(state)?.journey?.blocks ?? {}

  return Object.keys(blocks).reduce((acc, blockId) => {
    // if stepId is provided, only include blocks that belong to that step
    if (stepId && blocks[blockId].stepId !== stepId) {
      return acc
    }

    // check if the block is shown
    if (!selectIsBlockShown(state, blockId, undefined)) {
      acc[blockId] = blocks[blockId]
    }

    return acc
  }, {})
}

/**
 * Returns the blocks that should always be considered to be shown since
 * they're not associated with display conditions.
 */
export const selectInitiallyShownBlocks = (
  state: WritableAppState
): Set<BlockId> => {
  const selected = selectSelected(state)

  return computeInitiallyShownBlocks(
    selected?.journey?.blocks,
    selected?.displayConditionBlocks
  )
}

const computeInitiallyShownBlocks = memoLastCall(
  (
    blocks: Journey['blocks'],
    displayConditionBlocks: PerJourneyAppState['displayConditionBlocks']
  ) => {
    return new Set(
      Object.keys(blocks ?? {}).filter(
        (blockId) => displayConditionBlocks?.has(blockId) !== true
      )
    )
  }
)

/**
 * Returns the current Step.
 * If not set, returns the first one by order index.
 */
export const selectCurrentStep = (state: WritableAppState) => {
  if (state.navigatedPath.length) {
    const currentStepId = state.navigatedPath[state.navigatedPath.length - 1]

    return selectStepById(state, currentStepId)
  }

  return undefined
}

export const selectCurrentStepOrThrow = (state: WritableAppState) => {
  const step = selectCurrentStep(state)

  if (!step) throw new Error('Expected current Step to be set')

  return step
}

export const selectPreviousStep = (state: WritableAppState) => {
  if (state.navigatedPath.length >= 2) {
    const previousStepId = state.navigatedPath[state.navigatedPath.length - 2]

    return selectStepById(state, previousStepId)
  }

  return undefined
}

/**
 * Infers whether the user can navigate back to a previous Step,
 * which should only be possible if the user has visited at least 2 different Steps.
 */
export const selectCanNavigateBack = (state: WritableAppState) => {
  return state.navigatedPath.length >= 2
}

/**
 * Returns the cache of blocks map for the currently selected Journey.
 */
export const selectBlocksMapCache = (state: WritableAppState) => {
  return selectSelected(state)?.blocksMap
}

/**
 * Returns the raw contextual values as they've been parsed by AppParams.
 *
 * WARNING: This is different than {@link selectContextParameterValues}, which
 * further processes only the contextual parameters that are part of the Journey Config,
 * therefore expected.
 */
export const selectContextualValues = (state: WritableAppState) =>
  state.appParams?.contextualValues

/**
 * Loops through the contextual values and returns only those that are directly
 * associated with context parameters expected by the selected Journey.
 *
 * Otherwise returns an empty object.
 */
export const selectContextParameterValues = (
  state: WritableAppState
): ContextualValues => {
  const values = selectContextualValues(state)

  if (!values) return {}

  const paramsByKey = selectContextParametersByKey(state)

  return computeContextParameterValuesWithMemo(values, paramsByKey)
}

const computeContextParameterValuesWithMemo = memoLastCall(
  (
    values: ContextualValues,
    contextParametersByKey: ReturnType<typeof selectContextParametersByKey>
  ) => {
    if (!contextParametersByKey) return {}

    return Object.entries(values).reduce<ContextualValues>(
      (acc, [key, value]) => {
        if (typeof contextParametersByKey[key] !== 'undefined') {
          acc[key] = value
        }

        return acc
      },
      {}
    )
  }
)

/**
 * Throught approximation, attempts to compute the number of remaining Steps by
 * subtracting the number of visited and skipped Steps from the total number of Steps.
 *
 * The current Step is excluded from the count, meaning we expect 4 for a Journey composed of six steps,
 * whereas the current Step is the second (rationale is 6 - 2 = 4).
 *
 * Additionally, it also:
 *
 * - Relies exclusively on Step order index in case of previews
 * - Returns 1 if the current Step is the last one (i.e. contains a "submit" action)
 * - Returns 0 if the Journey has already been submitted
 *
 * TODO: unit test
 */
export const selectNumberOfRemainingSteps = (
  state: WritableAppState
): number => {
  // in preview mode, we compute remaining Steps exclusively based on order index
  if (state.appParams?.preview) {
    return (
      selectNumberOfSteps(state) -
      (selectCurrentStep(state)?.orderIndex ?? 0) -
      1
    )
  }

  if (selectSubmittedAt(state)) return 0
  if (selectIsSubmitStep(state)) return 1

  const skippedSteps = selectLastDesiredState(state)?.skipSteps

  return (
    selectNumberOfSteps(state) -
    (skippedSteps?.size ?? 0) -
    state.navigatedPath.length
  )
}

/**
 * Determines whether the current Step is of "submit" type.
 * This is inferred by whether the current Step contains an Action bar with a submit action.
 *
 * TODO: unit test
 */
export const selectIsSubmitStep = (state: WritableAppState): boolean => {
  const currentStep = selectCurrentStep(state)

  if (!currentStep) return false

  const step = selectStepById(state, currentStep.id)
  const blockIds = Object.keys(findLayoutZoneOfEachBlock(step?.layout))

  return blockIds.some((blockId) => {
    const block = selectBlockById(state, blockId)

    return (
      isActionBarBlock(block) &&
      block.settings.next?.action === ACTION_BAR_ACTION_TYPE.SUBMIT_AND_NAVIGATE
    )
  })
}

export const selectSubmissionStatus = (state: WritableAppState) => {
  return state.submissionState.status
}

export const selectIsSubmitted = (state: WritableAppState) => {
  return selectSubmissionStatus(state) === SUBMISSION_STATUS.SUCCESS
}

export const selectSubmittedAt = (state: WritableAppState) => {
  if (selectSubmissionStatus(state) === SUBMISSION_STATUS.SUCCESS) {
    return state.submissionState.submittedAt
  }

  return undefined
}

/**
 * Parses through a given block's references and returns their referenced block values
 * keyed by meaning and path (`stepId/blockName`).
 *
 * WARNING: Mostly for backwards compatibility purposee, should be avoided.
 *
 * @deprecated prefer {@link selectBlockReferenceValuesByMeaning} instead
 */
export const selectBlockReferencedValuesByPath = (
  state: WritableAppState,
  blockId: BlockId | undefined,
  scope?: string
) => {
  const block = selectBlockByIdOrScope(state, blockId, scope)

  if (block) {
    return Object.values(block.references ?? {})?.reduce<{
      [meaning: string]: {
        [key: string]: unknown
      }
    }>((acc, ref) => {
      if (isBlockReference(ref)) {
        const refBlock = selectBlockById(state, ref.referenceId)

        if (refBlock) {
          if (!acc[ref.meaning]) acc[ref.meaning] = {}

          const key = `${refBlock.stepId}/${refBlock.name}`

          acc[ref.meaning][key] = selectBlockValueById(state, refBlock.id)
        }
      }

      return acc
    }, {})
  }

  return {}
}

/**
 * Parses through a given Block references and return their referenced block values
 * grouped by meaning.
 *
 * TODO: unit test
 */
export const selectBlockReferenceValuesByMeaning = <
  T extends Reference = Reference
>(
  state: WritableAppState,
  blockId: BlockId | undefined,
  scope?: string
): Partial<
  Record<
    T['meaning'],
    Array<{
      block: Block
      value: BlockValue
    }>
  >
> => {
  const block = selectBlockByIdOrScope(state, blockId, scope)

  return Object.values(block?.references ?? {})?.reduce<
    ReturnType<typeof selectBlockReferenceValuesByMeaning>
  >((acc, ref) => {
    if (isBlockReference(ref)) {
      const refBlock = selectBlockById(state, ref.referenceId)

      if (refBlock) {
        // init the array
        if (!acc[ref.meaning]) acc[ref.meaning] = []

        acc[ref.meaning]!.push({
          block: refBlock,
          value: selectBlockValueById(state, refBlock.id)
        })
      }
    }

    return acc
  }, {})
}
